import React, { useEffect } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import { useLazyGetRulesAffectedByTopologyQuery } from "services/rules";

// AUDIT-TOPOLOGY-DRIFT: Shared confirmation dialog surfaced before any UI
// action that changes a device's parent-child relationships. Queries
// rulesMS for every rule that references this device and informs the user
// that those rules will be refreshed — associative rules whose peer list
// was derived from the current topology have their `associatedDevices`
// recomputed; if the device no longer has children, the rule is disabled
// (so the user notices and can revisit its configuration) rather than
// silently downgraded to a self-rule.
//
// Usage:
//   <ParentChildTopologyWarning
//     open={confirmOpen}
//     deviceId={device.internalId}
//     serviceId={service.id}
//     onCancel={() => setConfirmOpen(false)}
//     onConfirm={() => { setConfirmOpen(false); applyTopologyChange(); }}
//   />
export default function ParentChildTopologyWarning({
  open,
  deviceId,
  serviceId,
  onCancel,
  onConfirm,
}) {
  const token = window.localStorage.getItem("token");
  const [fetchAffected, { data, isFetching, isUninitialized }] =
    useLazyGetRulesAffectedByTopologyQuery();

  useEffect(() => {
    if (open && deviceId) {
      fetchAffected({ token, deviceId, serviceId });
    }
  }, [open, deviceId, serviceId]);

  const rawAffected = data?.payload || [];
  // Dedupe per-device fan-out of a global rule (same name across N devices
  // sharing one `globalId`). Fall back to `_id` for device-level rules.
  const affected = (() => {
    const seen = new Set();
    const out = [];
    for (const rule of rawAffected) {
      const key = rule.globalId || String(rule._id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(rule);
    }
    return out;
  })();
  // The backend `affectedByTopology` endpoint now only returns associative
  // rules, so every affected item is associative by construction — no need
  // to split into associative / other buckets.
  const associativeCount = affected.length;

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>Parent-child change</DialogTitle>
      <DialogContent>
        {isFetching || isUninitialized ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "24px",
            }}
          >
            <CircularProgress />
          </div>
        ) : affected.length === 0 ? (
          <p>
            No existing associative rules reference this device. The relationship change will
            apply without affecting any rules.
          </p>
        ) : (
          <>
            <p>
              <strong>
                {affected.length} associative rule{affected.length === 1 ? "" : "s"}
              </strong>{" "}
              {affected.length === 1 ? "references" : "reference"} this device.
              {" "}
              {associativeCount === 1 ? "Its" : "Their"} associated device
              list will be refreshed to match the updated parent-child
              relationships.{" "}
              <strong>
                If this device has no children after the change, the
                associative rule will be disabled for this specific
                device - other devices under the same rule are unaffected.
                It will be re-enabled automatically if a child is added
                to this device later.
              </strong>
            </p>
            <ul
              style={{
                maxHeight: "220px",
                overflowY: "auto",
                paddingLeft: "16px",
              }}
            >
              {affected.map((rule) => (
                <li key={rule._id}>
                  <strong>{rule.name || "(unnamed)"}</strong>
                  {rule.enabled === false ? " - already disabled" : ""}
                </li>
              ))}
            </ul>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="error">
          Cancel
        </Button>
        <Button onClick={onConfirm} color="secondary" disabled={isFetching}>
          Apply Change
        </Button>
      </DialogActions>
    </Dialog>
  );
}
